import { useEffect } from "react";
import { SITE_URL } from "../../api";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "../../routes/LangLayout";

const setMetaContent = (selector, value) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(selector.startsWith("link") ? "href" : "content", value);
};

// hreflang alternates have no static default in public/index.html (there's
// no single "default" to mutate — the whole point is one tag per language),
// so unlike the rest of this component they're inserted/removed for real
// rather than just mutated in place. Tagged with a data attribute (rather
// than wrapped in a container element, which <head> can't hold — browsers
// relocate non-metadata elements like <div> out of it) so a page swap can
// find and clear the previous set instead of accumulating stale tags.
const ALTERNATE_MARKER = "data-seo-alternate";

// If `path` starts with a supported language segment (/fr/..., /en/...,
// /ar/...), swap it to build each sibling URL — this is the whole reason the
// public routes are prefixed at all (see routes/LangLayout.js): one indexable
// URL per language, cross-linked via hreflang so search engines know they're
// translations of the same page rather than duplicate content.
const buildAlternates = (path) => {
  const match = path.match(/^\/(fr|en|ar)(\/.*)?$/);
  if (!match) return null;
  const rest = match[2] || "";
  return SUPPORTED_LANGS.map((lang) => ({ lang, url: `${SITE_URL}/${lang}${rest}` }));
};

const Seo = ({ title, description, path, image = "/images/saydalani_logo.png" }) => {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const imageUrl = image.startsWith("http") ? image : `${SITE_URL}${image}`;

    document.title = title;
    setMetaContent('meta[name="description"]', description || "");
    setMetaContent('link[rel="canonical"]', url);
    setMetaContent('meta[property="og:title"]', title);
    setMetaContent('meta[property="og:description"]', description || "");
    setMetaContent('meta[property="og:url"]', url);
    setMetaContent('meta[property="og:image"]', imageUrl);
    setMetaContent('meta[name="twitter:title"]', title);
    setMetaContent('meta[name="twitter:description"]', description || "");
    setMetaContent('meta[name="twitter:image"]', imageUrl);

    document.querySelectorAll(`link[${ALTERNATE_MARKER}]`).forEach((el) => el.remove());
    const alternates = buildAlternates(path);
    if (alternates) {
      const entries = [
        ...alternates,
        { lang: "x-default", url: alternates.find((a) => a.lang === DEFAULT_LANG)?.url || alternates[0].url },
      ];
      entries.forEach(({ lang, url: altUrl }) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = lang;
        link.href = altUrl;
        link.setAttribute(ALTERNATE_MARKER, "");
        document.head.appendChild(link);
      });
    }

    return () => document.querySelectorAll(`link[${ALTERNATE_MARKER}]`).forEach((el) => el.remove());
  }, [title, description, path, image]);

  return null;
};

export default Seo;
